import tokens from '@kluein/design-tokens';
import { Anchor, Box, type BoxProps } from '@kluein/klue-ui';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';
import styled from 'styled-components';

import { Logo } from 'components/common/logo';
import { useAuth } from 'contexts/auth';
import useAskKlueLog from 'hooks/dashboard/use-ask-klue-log/useAskKlueLog';
import { AnalyticsAction } from 'lib/analytics';
import { ChatStatusHelpers } from 'modules/shared/libs/chat/constants';
import {
  filterRivalsByCategory,
  getRandomPrompt,
  getTopTierRivals,
} from 'pages/dashboard/utils/askKlue.utils';
import store from 'store';
import TRACKING_IDS from 'tracking-ids';

import { askKLueSingleClickPrompts } from '../ask-klue-modal-wizard/data/askKlueWizardFlow';

import type {
  AskKlueEvent,
  AskKlueEventSuggestType,
} from 'hooks/dashboard/use-ask-klue-log/useAskKlueLog';
import type { UseAnswerChatReturnType } from 'modules/current-compete/libs/ask-klue-answer-mode-chat/hooks/useAnswerChat';
import type { UseResearchChatReturnType } from 'modules/current-compete/libs/ask-klue-research-mode-chat/hooks/useResearchChat';
import type { RivalSortedType } from 'pages/dashboard/utils/askKlue.utils.types';

const StyledBox = styled(Box)<{ isInitalScreen?: boolean }>`
  background-color: ${tokens.color.secondary[200]};
  border-radius: ${({ isInitalScreen }) => (isInitalScreen ? '8px' : '30px')};
  flex: ${({ isInitalScreen }) =>
    isInitalScreen ? '0 0 calc(50% - 6px)' : '0 0 auto'};
  padding: ${({ isInitalScreen }) =>
    isInitalScreen
      ? `${tokens.spacing.xsmall} ${tokens.spacing.small}`
      : `${tokens.spacing.small} ${tokens.spacing.large}`};
`;

const selectAllRivals = store.select(({ rivals: { all } }) => ({
  allRivals: all,
}));

function AskKlueSingleClickPrompt({
  handleAskKlue,
  isPage = false,
  isInitalScreen = false,
  isResponseLoading = false,
  activeChat,
  isResearchMode = false,
  disabled = false,
  onDidSubmitAskKlue,
}: {
  handleAskKlue: (question: string, logEventType: AskKlueEvent['type']) => void;
  isPage?: boolean;
  isInitalScreen?: boolean;
  isResponseLoading?: boolean;
  activeChat?: UseResearchChatReturnType | UseAnswerChatReturnType;
  isResearchMode?: boolean;
  disabled?: boolean;
  onDidSubmitAskKlue?: () => void;
}) {
  const logAskKlueEvent = useAskKlueLog();
  const { company } = useAuth();
  const battlecardPage = useMatch('/profile/:profileId/battlecard/*');
  const { allRivals } = useSelector(selectAllRivals);

  const rivalsList = allRivals.sort((rivalA, rivalB) =>
    rivalA.name.localeCompare(rivalB.name),
  );

  const getBattlecardProfile = useCallback(() => {
    if (!battlecardPage) return false;

    const profileId = battlecardPage.params.profileId as string;
    if (!profileId) return false;

    const rival = rivalsList.find((r) => r.profileId === parseInt(profileId));
    if (!rival) return false;

    if (company?.name.toLowerCase().includes(rival.name.toLowerCase()))
      return false;

    return Array.from({ length: 6 }, () => rival) as RivalSortedType[];
  }, [rivalsList, company, battlecardPage]);

  const promptList = useMemo(() => {
    const battlecardRivalList = getBattlecardProfile();
    const firstThreeRivals = battlecardRivalList
      ? battlecardRivalList
      : getTopTierRivals(
          filterRivalsByCategory(rivalsList, company?.name || ''),
        );
    const randomPromptIndex = getRandomPrompt(askKLueSingleClickPrompts.length);
    const singlePromptList = firstThreeRivals.map((rival) => {
      const promptIndex = randomPromptIndex.next().value || 0;
      const prompt = askKLueSingleClickPrompts[promptIndex];
      const rivalName = rival.name;
      return {
        template: prompt,
        rivalId: rival.id,
        rivalName,
        rivalRank: rival.rank || 1,
        text: prompt.replace('{COMPETITOR}', rivalName),
        iconUrl: rival.iconUrl,
      };
    }) as AskKlueEventSuggestType[];
    logAskKlueEvent({
      eventType: AnalyticsAction.renderAskSuggest,
      askKlueId: '',
      askKlueSuggest: singlePromptList,
    });
    return isInitalScreen || isPage
      ? singlePromptList.slice(0, 4)
      : singlePromptList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rivalsList]);

  const isDisabled = disabled || isResponseLoading;
  const disabledStyle = isDisabled
    ? { style: { opacity: 0.5, pointerEvents: 'none' as const } }
    : {};

  // eslint-disable-next-line no-nested-ternary
  const customStyles: BoxProps = isInitalScreen
    ? {
        align: 'center',
        justify: 'center',
        wrap: true,
        ...disabledStyle,
      }
    : isPage
      ? {
          margin: { top: 'small' },
          wrap: true,
          gap: { row: 'small', column: 'small' },
          ...disabledStyle,
        }
      : disabledStyle;

  return (
    <Box flex={{ grow: 1 }}>
      <Box
        gap={isInitalScreen ? undefined : 'small'}
        direction="row"
        flex={{ grow: 1 }}
        {...customStyles}
      >
        {promptList.map(({ text, iconUrl }, index) => (
          <StyledBox
            key={text}
            direction="row"
            align="center"
            gap="xsmall"
            margin={{
              bottom: isInitalScreen ? 'small' : undefined,
              right: isInitalScreen && index % 2 === 0 ? 'small' : undefined,
            }}
            isInitalScreen={isInitalScreen}
          >
            <Logo a11yTitle={text} src={iconUrl} size="small" />
            <Anchor
              size="medium"
              variant="undecorated-secondary"
              onClick={() => {
                // Route through the active chat whenever it exists (research
                // mode for curators, or answer mode). Outside the chat, fall
                // back to handleAskKlue, which opens the chat and queues it.
                if (activeChat) {
                  // Guard double-clicks / clicks while a response is still
                  // streaming so we don't enqueue duplicate submissions.
                  if (!ChatStatusHelpers.canSubmit(activeChat.status)) return;
                  // Sending straight to activeChat bypasses handleAskKlue,
                  // which is where the ask_suggest event is emitted. Log it
                  // explicitly here so prompt engagement is still reported.
                  logAskKlueEvent({
                    askKlueId: '',
                    eventType: AnalyticsAction.askSuggest,
                    text,
                  });
                  activeChat.sendMessage(text);
                  onDidSubmitAskKlue?.();
                } else {
                  handleAskKlue(text, AnalyticsAction.askSuggest);
                }
              }}
              data-tracking-id={TRACKING_IDS.askKlue.singleClickPromptButton}
              label={text}
            />
          </StyledBox>
        ))}
      </Box>
    </Box>
  );
}

export default AskKlueSingleClickPrompt;
